// Site-wide content (admin: Website → Navbar, footer & offices): navbar links,
// footer columns, phone numbers, addresses, social links, accreditation logos,
// offices on the coverage map. Shown on every page.
//   const { g, en, isFR } = useGlobalContent();   →  g.nav.links, g.footer …
// Empty admin fields fall back to src/data/globalDefaults.js (today's content).
import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SITE_PAGES from "../data/generated/sitePages.json";
import GLOBAL_DEFAULTS from "../data/globalDefaults";
import localizeServicePage from "../utils/localizeServicePage";
import fill from "../utils/fillDefaults";
import { resolvePromises, promisesFor } from "../utils/promises";
import { useEd } from "./Editable";

const GlobalCtx = createContext(null);

// Service-promise tokens ({urgent}, {quoteReply}, …) in the text are filled in from this
// content's own `promises` table; each coverage-map city uses its own city. `raw`: keep the
// tokens (the on-page editor, so editors keep them).
// The published page (and no page) is resolved once per language; drafts every time.
const cache = {};
export function resolveGlobal(page, isFR, { raw = false } = {}) {
  if (page && page !== SITE_PAGES.global) return resolveGlobalUncached(page, isFR, raw);
  const key = `${page ? "pub" : "none"}:${isFR ? "fr" : "en"}${raw ? ":raw" : ""}`;
  if (!cache[key]) cache[key] = resolveGlobalUncached(page, isFR, raw);
  return cache[key];
}

function resolveGlobalUncached(page, isFR, raw) {
  const defaults = isFR ? GLOBAL_DEFAULTS.fr : GLOBAL_DEFAULTS.en;
  const g = page ? fill(localizeServicePage(page, isFR).content, defaults) : defaults;
  if (raw) return g;
  const promises = promisesFor(g, isFR);
  const opts = { lang: isFR ? "fr" : "en", promises };
  const { promises: table, coverage, ...rest } = g;
  const out = resolvePromises(rest, opts);
  if (coverage) {
    const { cities, ...cov } = coverage;
    out.coverage = resolvePromises(cov, opts);
    if (cities) {
      out.coverage.cities = Object.fromEntries(Object.entries(cities).map(([key, v]) => [key, resolvePromises(v, { ...opts, city: key })]));
    }
  }
  out.promises = table;
  return out;
}

/** Used by the admin editor to show a draft; the public site reads the published content. */
export function GlobalContentProvider({ page, isFR, children }) {
  const { editing } = useEd();
  const value = useMemo(
    () => ({ g: resolveGlobal(page, isFR, { raw: editing }), en: resolveGlobal(page, false, { raw: editing }), isFR }),
    [page, isFR, editing]
  );
  return <GlobalCtx.Provider value={value}>{children}</GlobalCtx.Provider>;
}

/** { g, en, isFR } — g = site-wide content in the page language, en = English (shared values). */
export function useGlobalContent() {
  const ctx = useContext(GlobalCtx);
  const { pathname } = useLocation();
  const isFR = pathname.startsWith("/fr");
  const published = SITE_PAGES.global;
  const fallback = useMemo(() => ({ g: resolveGlobal(published, isFR), en: resolveGlobal(published, false), isFR }), [published, isFR]);
  return ctx || fallback;
}
